'use client'

import * as React from 'react'
import { useInView, useReducedMotion, animate } from 'framer-motion'
import { EASE } from '@/lib/motion'

// Counts a number up from 0 to `value` the first time it scrolls into
// view. Numbers that animate read as measured, not decorative. Under
// reduced motion it renders the final value immediately. Wrap the numeric
// part only; pass non-numeric units via prefix/suffix (e.g. "+", "%", " KB").

export function CountUp({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 1.1,
  className,
}: {
  value: number
  prefix?: string
  suffix?: string
  decimals?: number
  duration?: number
  className?: string
}) {
  const ref = React.useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const [display, setDisplay] = React.useState(0)

  React.useEffect(() => {
    if (reduce) {
      setDisplay(value)
      return
    }
    if (!inView) return
    const controls = animate(0, value, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(v),
    })
    return () => controls.stop()
  }, [inView, reduce, value, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toFixed(decimals)}
      {suffix}
    </span>
  )
}
